/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React from 'react';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '../../../../components/dashboard';
import Table, { TableColumn } from '../../../../components/table/Table';
import { AdminBreadcrumbs } from '../../../../routes/admin-dashboard/AdminBreadcrumbs';
import { useAppSelector } from '../../../../reducers';
import { FeaturePermissions } from '../../../../services/permissions/FeaturePermissions';
import { useRouteMatch } from 'react-router-dom';
import { Badge } from 'reactstrap';
import './styles.scss';
import { SubscriptionDetails } from './subscription-details';
import { QUESTION } from '../../../../constants/terminology';
import { UserLimitUpgradeModal } from '../../components/user-limit/UserLimitUpgradeModal';
import { FeatureCode } from '@g17eco/core';
import { AddonButton } from '../../components/payment-details/AddonButton';
import LoadingPlaceholder from '../../../../components/LoaderContainer/LoadingPlaceholder';
import { TrialModal } from '../../components/payment-details/TrialModal';
import { CustomMetricUpgradeModal } from '../../components/custom-metrics/CustomMetricUpgradeModal';

const getColumns = (title: string): TableColumn[] => {
  return [
    {
      Header: title,
      accessor: 'name',
      disableSortBy: true,
      cellProps: {
        className: 'name-col'
      }
    },
    {
      Header: '',
      accessor: 'data',
      disableSortBy: true,
      cellProps: {
        className: 'text-right'
      }
    },
  ];
}

export const SubscriptionInfoRoute = () => {

  const routeMatch = useRouteMatch<{ initiativeId: string }>();
  const organizationId = routeMatch.params.initiativeId;

  const state = useAppSelector(state => state);

  const globalData = useAppSelector(state => state.globalData);

  if (!globalData.loaded) {
    return <LoadingPlaceholder height={300} isLoading={true} />
  }

  const rootOrg = globalData.organization;

  const limitsRows = [
    {
      name: 'Number of users',
      data: (
        <div className={'align-items-center d-flex justify-content-end'}>
          <AddonButton
            size={'xs'}
            rootOrg={rootOrg}
            appConfig={globalData.data.appConfig}
            featureCode={FeatureCode.Users}
            TrialModalComponent={({ toggle }) => (
              <TrialModal
                title={'Additional user seats unavailable during trial'}
                toggle={toggle}
                initiativeId={rootOrg._id}>
                During the free trial, the option to buy additional user seats is not available.
                To buy more seats, first add credit card details or another payment method.
              </TrialModal>
            )}
            UpgradeModal={UserLimitUpgradeModal}
          >
            <i className={'fa fa-plus mr-1'} />Add user seats
          </AddonButton>
          <Limit getLimit={() => FeaturePermissions.getLimitUsers(state)} />
        </div>
      )
    },
    {
      name: 'Number of subsidiaries',
      data: <Limit getLimit={() => FeaturePermissions.getLimitReportingLevels(state)} />
    },
    {
      name: 'Number of surveys',
      data: <Limit getLimit={() => FeaturePermissions.getLimitSurveys(state)} />
    },
    {
      name: 'Number of custom metrics',
      data: (
        <div className={'align-items-center d-flex justify-content-end'}>
          <AddonButton
            size={'xs'}
            rootOrg={rootOrg}
            appConfig={globalData.data.appConfig}
            featureCode={FeatureCode.CustomMetrics}
            TrialModalComponent={({ toggle }) => (
              <TrialModal toggle={toggle} initiativeId={rootOrg._id}>
                During the free trial, the option to buy additional custom metrics is not available.
                To buy more custom metrics, first add credit card details or another payment method.
              </TrialModal>
            )}
            UpgradeModal={CustomMetricUpgradeModal}
          >
            <i className={'fa fa-plus mr-1'} />Buy custom metrics
          </AddonButton>
          <Limit getLimit={() => FeaturePermissions.getLimitCustomMetric(state)} />
        </div>
      )
    },
    {
      name: 'Number of global standards',
      data: <Limit getLimit={() => 0} />
    },
  ];

  const dashboardRows = [
    {
      name: 'Access to Insights and Trends',
      data: <Tick isEnabled={() => true} />
    },
    {
      name: 'Access to Downloads',
      data: <Tick isEnabled={() => FeaturePermissions.canAccessDownloads(state)} />
    },
    {
      name: 'Access to survey admin',
      data: <Tick isEnabled={() => FeaturePermissions.canAccessAdminDashboard(state)} />
    },
  ];

  const featureRows = [
    // Still early to display as feature, need to enable once stable, preparing here
    // {
    //   name: 'Bulk delegation (Beta)',
    //   data: <Tick isEnabled={() => FeaturePermissions.canAccessBulkDelegation(state)} />
    // },
    {
      name: 'Bulk import data',
      data: <Tick isEnabled={() => FeaturePermissions.canAccessBulkImporting(state)} />
    },
    {
      name: 'Enhanced reports: subsidiary & date comparison',
      data: <Tick isEnabled={() => FeaturePermissions.canAccessCombinedReport(state)} />
    },
    {
      name: 'Create combined surveys',
      data: <Tick isEnabled={() => FeaturePermissions.canAccessCombinedSurvey(state)} />
    },
    {
      name: 'Create survey templates',
      data: <Tick isEnabled={() => FeaturePermissions.canAccessSurveyTemplates(state)} />
    },
    {
      name: `${QUESTION.CAPITALIZED_SINGULAR} verification workflow`,
      data: <Tick isEnabled={() => FeaturePermissions.canAccessVerification(state)} />
    },
    {
      name: 'External assurance',
      data: <Tick isEnabled={() => FeaturePermissions.canAccessAssurance(state)} />
    },
  ];

  const advancedFeatureRows = [
    {
      name: 'Single-Sign-On (SSO)',
      data: <PleaseContactUs />
    },
    {
      name: 'API for data import',
      data: <PleaseContactUs />
    },
    {
      name: 'Sustainability consulting',
      data: <PleaseContactUs />
    },
  ];

  return (
    <Dashboard className='flex-fill subscription-info-dashboard'>
      <DashboardRow>
        <AdminBreadcrumbs breadcrumbs={[{ label: 'Subscription info' }]} initiativeId={organizationId} />
      </DashboardRow>
      <DashboardSectionTitle title='Subscription info' />
      <DashboardSection title='Your plan details:'>
        <div className='mx-auto w-75'>
          <SubscriptionDetails />
          <div className='mt-4'>
            <Table columns={getColumns('Limits')} data={limitsRows} />
          </div>
          <div className='mt-4'>
            <Table columns={getColumns('Dashboard/screens')} data={dashboardRows} />
          </div>
          <div className='mt-4'>
            <Table columns={getColumns('Features')} data={featureRows} />
          </div>
          <div className='mt-4'>
            <Table columns={getColumns('Advanced features')} data={advancedFeatureRows} />
          </div>
        </div>
      </DashboardSection>
    </Dashboard>
  );
}

const Tick = ({ isEnabled }: { isEnabled: () => boolean }) => {
  if (isEnabled()) {
    return <h4 className='m-0'><i className='fal fa-check text-ThemeSuccessMedium' /></h4>;
  }
  return <h4 className='m-0'><i className='fal fa-times text-ThemeDangerMedium' /></h4>;
}

const Limit = ({ getLimit }: { getLimit: () => number }) => {
  const limit = getLimit();

  return <Badge pill>{limit <= 0 ? 'Unlimited' : limit}</Badge>;
}

const PleaseContactUs = () => {
  return <span className='text-lg text-ThemeTextLight'>Contact us</span>;
}
